import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import Toasts from "../../../core/common/toast";

interface AdminProfileData {
  id?: number | string;
  first_name?: string;
  last_name?: string;
  username?: string;
  email?: string;
  phone?: string;
  password?: string;
  dob?: string;
  role?: string;
  verified?: boolean;
  created_at?: string;
  modified_at?: string;
  login_failed?: number;
  otp_code?: string | null;
  otp_expires_at?: string | null;
  token?: string;
  token_expires_at?: string | null;
  device_token?: string | null;
  device_id?: string | null;
  device_type?: string | null;
  [key: string]: any;
}

const AdminProfile = () => {
  const routes = all_routes;
  const [profileData, setProfileData] = useState<AdminProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roleInfo, setRoleInfo] = useState<any>(null);

  const fetchAdminLoginData = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const response = await Api.post(API_URLS.LOGIN_DATA, {});
      console.log("Admin login data response:", response);

      const resData = response?.data;
      const dataObj = resData?.data?.data || resData?.data || resData;

      if (dataObj) {
        setProfileData(dataObj);
        if (resData?.data?.hrms_role) {
          setRoleInfo(resData.data.hrms_role);
        }

        const existingUser = localStorage.getItem("user");
        let mergedUser = dataObj;
        if (existingUser && existingUser !== "undefined") {
          try {
            mergedUser = { ...JSON.parse(existingUser), ...dataObj };
          } catch (e) {}
        }
        localStorage.setItem("user", JSON.stringify(mergedUser));
      }

      // Read allowed permissions from localStorage or response
      const storedPerms = localStorage.getItem("allowed_permissions");
      if (storedPerms) {
        try {
          setPermissions(JSON.parse(storedPerms));
        } catch (e) {
          console.error("Error parsing stored permissions", e);
        }
      }
    } catch (err: any) {
      console.error("Error fetching admin login data:", err);
      const msg = Api.getErrorMessage(err) || "Failed to load admin profile data";
      setErrorMsg(msg);

      // Fallback to localStorage user if available
      const localUser = localStorage.getItem("user");
      if (localUser && localUser !== "undefined") {
        try {
          setProfileData(JSON.parse(localUser));
        } catch (e) { }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminLoginData();
  }, []);

  const getInitials = (firstName?: string, lastName?: string, username?: string) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.slice(0, 2).toUpperCase();
    }
    if (username) {
      return username.slice(0, 2).toUpperCase();
    }
    return "AD";
  };

  const fullName = profileData
    ? `${profileData.first_name || ""} ${profileData.last_name || ""}`.trim() || profileData.username || "Admin User"
    : "Admin User";

  const initials = getInitials(profileData?.first_name, profileData?.last_name, profileData?.username);

  return (
    <div className="page-wrapper">
      {successMsg && (
        <Toasts
          show={!!successMsg}
          message={successMsg}
          title={"Success"}
          type={"success"}
        />
      )}
      {errorMsg && (
        <Toasts
          show={!!errorMsg}
          message={errorMsg}
          title={"Error"}
          type={"danger"}
        />
      )}

      <div className="content">
        {/* Breadcrumb Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h6 className="fw-medium d-inline-flex align-items-center mb-3 mb-sm-0">
              <Link to={routes.adminDashboard}>
                <i className="ti ti-arrow-left me-2" />
                Admin Dashboard
              </Link>
            </h6>
            <h3 className="page-title mb-0 mt-1">Admin Profile</h3>
          </div>

          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <button
              onClick={fetchAdminLoginData}
              className="btn btn-outline-primary btn-sm d-flex align-items-center me-2"
              disabled={loading}
            >
              <i className={`ti ti-refresh me-1 ${loading ? "spin-icon" : ""}`} />
              Refresh Data
            </button>
            <div className="head-icons ms-1">
              <CollapseHeader />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading Admin Profile Details...</p>
            </div>
          </div>
        ) : (
          <div className="row">
            {/* Left Sidebar Profile Overview Card */}
            <div className="col-xl-4 col-lg-5 col-md-12">
              <div className="card card-bg-1 shadow-sm border-0 mb-4">
                <div className="card-body p-4 text-center">
                  <div className="avatar-container position-relative d-flex justify-content-center mb-3">
                    <span
                      className="avatar avatar-xxl rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold fs-24 shadow-sm"
                      style={{ width: "96px", height: "96px" }}
                    >
                      {initials}
                    </span>
                  </div>

                  <h4 className="fw-bold mb-1 text-uppercase text-dark">
                    {fullName}
                  </h4>

                  <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                    <span className="badge bg-purple-light text-purple fw-semibold px-3 py-1 fs-12">
                      <i className="ti ti-shield-check me-1" />
                      {profileData?.role || "ADMIN"}
                    </span>
                    <span
                      className={`badge ${profileData?.verified ? "bg-success-light text-success" : "bg-warning-light text-warning"
                        } fw-semibold px-3 py-1 fs-12`}
                    >
                      <i className={`ti ${profileData?.verified ? "ti-circle-check" : "ti-alert-circle"} me-1`} />
                      {profileData?.verified ? "Verified Account" : "Unverified"}
                    </span>
                  </div>

                  <div className="border-top pt-3 text-start">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="text-muted fs-13 text-uppercase fw-medium">User ID</span>
                      <span className="fw-bold text-dark fs-14">#{profileData?.id || "N/A"}</span>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="text-muted fs-13 text-uppercase fw-medium">Username</span>
                      <span className="fw-semibold text-dark fs-14">{profileData?.username || "N/A"}</span>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="text-muted fs-13 text-uppercase fw-medium">Email Address</span>
                      <span className="fw-semibold text-dark fs-14 text-break text-end" style={{ maxWidth: "200px" }}>
                        {profileData?.email || "N/A"}
                      </span>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="text-muted fs-13 text-uppercase fw-medium">Contact Phone</span>
                      <span className="fw-semibold text-dark fs-14">{profileData?.phone || "N/A"}</span>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="text-muted fs-13 text-uppercase fw-medium">Date of Birth</span>
                      <span className="fw-semibold text-dark fs-14">
                        {profileData?.dob ? moment(profileData.dob).format("DD-MM-YYYY") : "N/A"}
                      </span>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="text-muted fs-13 text-uppercase fw-medium">Created On</span>
                      <span className="fw-semibold text-dark fs-13">
                        {profileData?.created_at ? moment(profileData.created_at).format("DD-MM-YYYY HH:mm") : "N/A"}
                      </span>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-0">
                      <span className="text-muted fs-13 text-uppercase fw-medium">Last Modified</span>
                      <span className="fw-semibold text-dark fs-13">
                        {profileData?.modified_at ? moment(profileData.modified_at).format("DD-MM-YYYY HH:mm") : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Detailed Information Panels */}
            <div className="col-xl-8 col-lg-7 col-md-12">
              <div className="accordion accordions-items-seperate" id="adminProfileAccordion">

                {/* Section 1: General & Account Info */}
                <div className="accordion-item shadow-sm border-0 mb-3">
                  <div className="accordion-header" id="headingAdminInfo">
                    <div className="accordion-button bg-white py-3">
                      <div className="d-flex align-items-center flex-fill">
                        <i className="ti ti-user-check text-primary fs-20 me-2" />
                        <h5 className="text-uppercase mb-0 fw-bold fs-15 text-dark">
                          Account & Profile Information
                        </h5>
                        <Link
                          to="#"
                          className="d-flex align-items-center collapsed collapse-arrow ms-auto"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseAdminInfo"
                          aria-expanded="true"
                          aria-controls="collapseAdminInfo"
                        >
                          <i className="ti ti-chevron-down fs-18 text-muted" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div
                    id="collapseAdminInfo"
                    className="accordion-collapse collapse show border-top"
                    aria-labelledby="headingAdminInfo"
                    data-bs-parent="#adminProfileAccordion"
                  >
                    <div className="accordion-body p-4">
                      <div className="row g-3">
                        <div className="col-md-6 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">First Name</span>
                            <span className="fw-bold text-dark fs-15">{profileData?.first_name || "N/A"}</span>
                          </div>
                        </div>

                        <div className="col-md-6 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Last Name</span>
                            <span className="fw-bold text-dark fs-15">{profileData?.last_name || "N/A"}</span>
                          </div>
                        </div>

                        <div className="col-md-6 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Username</span>
                            <span className="fw-bold text-dark fs-15">{profileData?.username || "N/A"}</span>
                          </div>
                        </div>

                        <div className="col-md-6 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Email Address</span>
                            <span className="fw-bold text-dark fs-15 text-break">{profileData?.email || "N/A"}</span>
                          </div>
                        </div>

                        <div className="col-md-6 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Phone Number</span>
                            <span className="fw-bold text-dark fs-15">{profileData?.phone || "N/A"}</span>
                          </div>
                        </div>

                        <div className="col-md-6 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Administrative Role</span>
                            <span className="fw-bold text-primary fs-15">{profileData?.role || "ADMIN"}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Security & Authentication */}
                {/* <div className="accordion-item shadow-sm border-0 mb-3">
                  <div className="accordion-header" id="headingSecurityInfo">
                    <div className="accordion-button bg-white py-3">
                      <div className="d-flex align-items-center flex-fill">
                        <i className="ti ti-lock-access text-danger fs-20 me-2" />
                        <h5 className="text-uppercase mb-0 fw-bold fs-15 text-dark">
                          Security & Authentication Details
                        </h5>
                        <Link
                          to="#"
                          className="d-flex align-items-center collapse-arrow ms-auto"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseSecurityInfo"
                          aria-expanded="false"
                          aria-controls="collapseSecurityInfo"
                        >
                          <i className="ti ti-chevron-down fs-18 text-muted" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div
                    id="collapseSecurityInfo"
                    className="accordion-collapse collapse show border-top"
                    aria-labelledby="headingSecurityInfo"
                    data-bs-parent="#adminProfileAccordion"
                  >
                    <div className="accordion-body p-4">
                      <div className="row g-3">
                        <div className="col-md-6 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Failed Login Attempts</span>
                            <span className={`fw-bold fs-15 ${profileData?.login_failed ? "text-danger" : "text-success"}`}>
                              {profileData?.login_failed ?? 0} Attempts
                            </span>
                          </div>
                        </div>

                        <div className="col-md-6 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Account Verification</span>
                            <span className={`fw-bold fs-15 ${profileData?.verified ? "text-success" : "text-warning"}`}>
                              {profileData?.verified ? "Verified" : "Pending Verification"}
                            </span>
                          </div>
                        </div>

                        <div className="col-md-6 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">OTP Code Status</span>
                            <span className="fw-semibold text-dark fs-14">
                              {profileData?.otp_code ? "Active OTP Generated" : "No Active OTP"}
                            </span>
                          </div>
                        </div>

                        <div className="col-md-6 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">OTP Expiration</span>
                            <span className="fw-semibold text-dark fs-14">
                              {profileData?.otp_expires_at ? moment(profileData.otp_expires_at).format("DD-MM-YYYY HH:mm:ss") : "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Password Hash</span>
                            <span className="font-monospace text-muted fs-12 text-break">
                              {profileData?.password ? `${profileData.password.slice(0, 20)}...` : "Encrypted (bcrypt)"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}

                {/* Section 3: Device & System Context */}
                {/* <div className="accordion-item shadow-sm border-0 mb-3">
                  <div className="accordion-header" id="headingDeviceInfo">
                    <div className="accordion-button bg-white py-3">
                      <div className="d-flex align-items-center flex-fill">
                        <i className="ti ti-devices text-info fs-20 me-2" />
                        <h5 className="text-uppercase mb-0 fw-bold fs-15 text-dark">
                          Device & Session Information
                        </h5>
                        <Link
                          to="#"
                          className="d-flex align-items-center collapse-arrow ms-auto"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseDeviceInfo"
                          aria-expanded="false"
                          aria-controls="collapseDeviceInfo"
                        >
                          <i className="ti ti-chevron-down fs-18 text-muted" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div
                    id="collapseDeviceInfo"
                    className="accordion-collapse collapse show border-top"
                    aria-labelledby="headingDeviceInfo"
                    data-bs-parent="#adminProfileAccordion"
                  >
                    <div className="accordion-body p-4">
                      <div className="row g-3">
                        <div className="col-md-4 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Device Type</span>
                            <span className="fw-bold text-dark fs-14">{profileData?.device_type || "Web / Browser"}</span>
                          </div>
                        </div>

                        <div className="col-md-4 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Device ID</span>
                            <span className="fw-bold text-dark fs-14">{profileData?.device_id || "N/A"}</span>
                          </div>
                        </div>

                        <div className="col-md-4 col-sm-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Token Expiration</span>
                            <span className="fw-bold text-dark fs-14">
                              {profileData?.token_expires_at ? moment(profileData.token_expires_at).format("DD-MM-YYYY HH:mm") : "Active Session"}
                            </span>
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="p-3 bg-light rounded border">
                            <span className="text-muted fs-12 text-uppercase d-block mb-1 fw-semibold">Device Token</span>
                            <span className="font-monospace text-muted fs-12 text-break">
                              {profileData?.device_token || "None registered"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}

                {/* Section 4: Assigned Permissions */}
                {/* {permissions.length > 0 && (
                  <div className="accordion-item shadow-sm border-0 mb-3">
                    <div className="accordion-header" id="headingPermissions">
                      <div className="accordion-button bg-white py-3">
                        <div className="d-flex align-items-center flex-fill">
                          <i className="ti ti-key text-success fs-20 me-2" />
                          <h5 className="text-uppercase mb-0 fw-bold fs-15 text-dark">
                            Assigned System Permissions ({permissions.length})
                          </h5>
                          <Link
                            to="#"
                            className="d-flex align-items-center collapse-arrow ms-auto"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapsePermissions"
                            aria-expanded="false"
                            aria-controls="collapsePermissions"
                          >
                            <i className="ti ti-chevron-down fs-18 text-muted" />
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div
                      id="collapsePermissions"
                      className="accordion-collapse collapse border-top"
                      aria-labelledby="headingPermissions"
                      data-bs-parent="#adminProfileAccordion"
                    >
                      <div className="accordion-body p-4">
                        <div className="d-flex flex-wrap gap-2">
                          {permissions.map((perm, idx) => (
                            <span key={idx} className="badge bg-light text-dark border px-3 py-2 fs-12 fw-medium">
                              <i className="ti ti-check text-success me-1" />
                              {perm}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )} */}

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProfile;
